import { ApiError } from '@/types/api.types';

/** Gateways reporting that they could not reach the service behind them. */
const CONNECTIVITY_STATUSES: readonly number[] = [502, 503, 504];

/**
 * Whether a failed request means the portal could not reach the backend at all, as opposed to the
 * backend answering and refusing (PP-028 / proweb-74).
 *
 * The two are not the same thing to a provider. A 404 or a 429 is an answer — the policy does not
 * match, the attempt budget is spent — and saying "the service is unavailable" for either is the
 * defect fixed under PROWEB-123. What this covers is the case where nothing answered: a timeout, a
 * dropped connection, or a gateway that could not reach the service behind it.
 *
 * A bare 500 is deliberately NOT here. The backend answered; something inside it failed. That is a
 * fault for the affected section to report, not a connectivity outage for the whole portal.
 */
export function isConnectivityFailure(error: unknown): boolean {
  if (error === null || error === undefined) {
    return false;
  }

  const status = statusOf(error);

  // No status at all means nothing ever answered: the axios timeout this grew out of, an aborted
  // request, a DNS failure.
  return status === undefined || CONNECTIVITY_STATUSES.includes(status);
}

/**
 * Read by shape, not by type. Every failure the api-client produces is an ApiError, but reading the
 * status only off that class made any other error object look like a dead connection regardless of
 * what it actually carried — including the plain rejections tests hand the query layer.
 */
function statusOf(error: unknown): number | undefined {
  if (error instanceof ApiError) {
    return error.status;
  }

  if (typeof error === 'object' && error !== null && 'status' in error) {
    const { status } = error;
    return typeof status === 'number' ? status : undefined;
  }

  return undefined;
}
